"use client";

import { revalidateLogic, useForm } from "@tanstack/react-form";
import { Mail } from "lucide-react";
import { useMemo } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { PhoneNumberInput } from "@/components/ui/phone-number-input";
import { normalizePaymentPhoneNumber } from "@/lib/phone-number";
import { cn } from "@/lib/utils";

/**
 * Assign one seat to somebody who is not on Hiive.
 *
 * ## Why this is the primary affordance on an unassigned row
 *
 * Booking for people who don't have the app is the common case, not the edge
 * one — a group of five has one person paying and four who have never heard of
 * us. Tagging a Hiive member and taking the seat yourself are the *narrow*
 * cases, so they sit above this as two compact buttons while the name+email
 * fields stay open and visible.
 *
 * ## Validation
 *
 * Mirrors the server's `attendeeInputSchema` guest branch. Silent until the
 * first submit, then revalidating on change — and `onDynamic` is the ONLY
 * validator key, because an `onChange`/`onBlur` pair leaves a stale
 * `errorMap.onBlur` that only a real blur can clear, which pins an error
 * forever on a field the person never blurs.
 *
 * The email is deliberately shape-checked only. A typo that is still a valid
 * address is undetectable here, and the venue can resend from the dashboard —
 * that is the accepted trade for not making guests sign up.
 */

const guestAttendeeSchema = z.object({
  guestName: z
    .string()
    .trim()
    .min(1, "Enter their name")
    .max(120, "That name is too long"),
  guestEmail: z
    .string()
    .trim()
    .toLowerCase()
    .max(254, "That email is too long")
    .email("Enter a valid email"),
  guestPhone: z
    .string()
    .trim()
    .max(40)
    .optional()
    .refine(
      (value) => !value || Boolean(normalizePaymentPhoneNumber(value)),
      "Enter a valid phone number",
    ),
  guestGender: z.enum(["male", "female"]).optional(),
});

/** A balanced ticket counts every seat on a side, so the side is required. */
const balancedGuestAttendeeSchema = guestAttendeeSchema.refine(
  (value) => Boolean(value.guestGender),
  { message: "Choose man or woman", path: ["guestGender"] },
);

const GENDER_OPTIONS = [
  { value: "male", label: "Man" },
  { value: "female", label: "Woman" },
] as const;

export type GuestAttendeeValues = z.infer<typeof guestAttendeeSchema>;

export function GuestAttendeeForm({
  onConfirm,
  disabled = false,
  disabledReason,
  seatLabel,
  interceptSubmit,
  askGender = false,
  fullSides = [],
}: {
  onConfirm: (values: GuestAttendeeValues) => void;
  disabled?: boolean;
  /**
   * Shown in place of the fields when guests are not allowed. A disabled
   * control with a stated reason reads as a rule; a hidden one reads as a
   * missing feature.
   */
  disabledReason?: string | null;
  /** For the a11y label — "Ticket 2" etc, so ids stay unique per row. */
  seatLabel: string;
  /**
   * Take over the submit. Return `true` to swallow it — used while signed out,
   * where the guest details cannot be carried through the checkout hand-off
   * and silently accepting them would lose them.
   */
  interceptSubmit?: () => boolean;
  /**
   * The ticket keeps the crowd balanced: ask which side this guest is on,
   * since they have no profile to read it from.
   */
  askGender?: boolean;
  /**
   * Sides with no seat left for this guest. Their option is disabled, so a
   * buyer can't put a woman on a full women's side by accident.
   */
  fullSides?: ReadonlyArray<"male" | "female">;
}) {
  const schema = useMemo(
    () => (askGender ? balancedGuestAttendeeSchema : guestAttendeeSchema),
    [askGender],
  );
  const form = useForm({
    defaultValues: {
      guestName: "",
      guestEmail: "",
      guestPhone: "",
      guestGender: undefined,
    } as GuestAttendeeValues,
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => {
      // The side may have filled after it was picked; the note says so.
      if (value.guestGender && fullSides.includes(value.guestGender)) return;
      onConfirm({
        guestName: value.guestName.trim(),
        guestEmail: value.guestEmail.trim().toLowerCase(),
        guestPhone: value.guestPhone
          ? (normalizePaymentPhoneNumber(value.guestPhone) ?? undefined)
          : undefined,
        guestGender: askGender ? value.guestGender : undefined,
      });
      form.reset();
    },
  });

  if (disabled && disabledReason) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-background-alt/50 px-4 py-3">
        <p className="flex items-start gap-2 text-sm text-text-secondary">
          <Mail className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {disabledReason}
        </p>
      </div>
    );
  }

  const fieldId = (name: string) =>
    `${seatLabel.replace(/\s+/g, "-").toLowerCase()}-${name}`;

  return (
    <form
      // Nested inside the booking page's own layout, which is not a form — but
      // this must not become one either, or Enter in a field would submit the
      // booking. `noValidate` leaves the messaging to the schema.
      noValidate
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault();
        submitEvent.stopPropagation();
        if (interceptSubmit?.()) return;
        void form.handleSubmit();
      }}
      className="space-y-3"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <form.Field name="guestName">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={fieldId("name")}>Full name</FieldLabel>
              <Input
                id={fieldId("name")}
                autoComplete="off"
                placeholder="Rahul Menon"
                value={field.state.value}
                onChange={(changeEvent) =>
                  field.handleChange(changeEvent.target.value)
                }
                onBlur={field.handleBlur}
                disabled={disabled}
                className="h-11 rounded-xl"
              />
              {field.state.meta.errors.length > 0 ? (
                <FieldError errors={field.state.meta.errors} />
              ) : null}
            </Field>
          )}
        </form.Field>

        <form.Field name="guestEmail">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={fieldId("email")}>Email</FieldLabel>
              <Input
                id={fieldId("email")}
                type="email"
                inputMode="email"
                autoComplete="off"
                placeholder="rahul@example.com"
                value={field.state.value}
                onChange={(changeEvent) =>
                  field.handleChange(changeEvent.target.value)
                }
                onBlur={field.handleBlur}
                disabled={disabled}
                className="h-11 rounded-xl"
              />
              {field.state.meta.errors.length > 0 ? (
                <FieldError errors={field.state.meta.errors} />
              ) : null}
            </Field>
          )}
        </form.Field>
      </div>

      {askGender ? (
        <form.Field name="guestGender">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel id={fieldId("gender-label")}>Gender</FieldLabel>
              <fieldset
                aria-labelledby={fieldId("gender-label")}
                className="grid grid-cols-2 gap-2"
              >
                {GENDER_OPTIONS.map((option) => {
                  const full = fullSides.includes(option.value);
                  const selected = field.state.value === option.value;
                  return (
                    <label
                      key={option.value}
                      className={cn(
                        "flex h-11 items-center justify-center rounded-xl border text-sm font-medium transition-colors duration-150 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand/40",
                        full
                          ? "cursor-not-allowed border-dashed border-border text-text-tertiary"
                          : selected
                            ? "cursor-pointer border-brand bg-brand/10 text-text-primary"
                            : "cursor-pointer border-border text-text-secondary pointer-fine:hover:bg-surface-alt",
                      )}
                    >
                      <input
                        type="radio"
                        name={fieldId("gender")}
                        value={option.value}
                        checked={selected}
                        disabled={disabled || full}
                        onChange={() => field.handleChange(option.value)}
                        className="sr-only"
                      />
                      {full ? `${option.label} · full` : option.label}
                    </label>
                  );
                })}
              </fieldset>
              <FieldDescription>
                {fullSides.length === 2
                  ? "Both sides are full right now, so this ticket can't go to anyone else. Remove it, or join the waitlist."
                  : fullSides.length === 1
                    ? `${fullSides[0] === "female" ? "Women's" : "Men's"} spots are full right now, so this ticket can only go to a ${fullSides[0] === "female" ? "man" : "woman"}.`
                    : "This ticket keeps the crowd balanced, so every seat counts as a man or a woman."}
              </FieldDescription>
              {field.state.value && fullSides.includes(field.state.value) ? (
                <FieldError>
                  {field.state.value === "female" ? "Women's" : "Men's"} spots
                  just filled up. Choose again.
                </FieldError>
              ) : null}
              {field.state.meta.errors.length > 0 ? (
                <FieldError errors={field.state.meta.errors} />
              ) : null}
            </Field>
          )}
        </form.Field>
      ) : null}

      {/*
        Optional, and genuinely used: the venue's door list, the staff search and
        the CSV export all read this column. It was in the schema, the database,
        the index and every one of those surfaces already — but no input ever
        rendered it, so the column was always empty and the phone column on a
        guest row read as broken data rather than as a field nobody was asked for.
      */}
      <form.Field name="guestPhone">
        {(field) => (
          <Field data-invalid={field.state.meta.errors.length > 0}>
            <FieldLabel htmlFor={fieldId("phone")}>
              Mobile number{" "}
              <span className="font-normal text-text-tertiary">(optional)</span>
            </FieldLabel>
            <PhoneNumberInput
              id={fieldId("phone")}
              autoComplete="off"
              value={field.state.value}
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              aria-invalid={field.state.meta.errors.length > 0}
              disabled={disabled}
              className="h-11 rounded-xl"
            />
            <FieldDescription>
              Only so the venue can reach them on the day. We don't text
              tickets.
            </FieldDescription>
            {field.state.meta.errors.length > 0 ? (
              <FieldError errors={field.state.meta.errors} />
            ) : null}
          </Field>
        )}
      </form.Field>

      <FieldDescription>
        We'll email them their ticket right away. They can show the QR at the
        door — no app needed.
      </FieldDescription>

      <Button
        type="submit"
        variant="solid"
        disabled={disabled}
        className="h-11 w-full rounded-full font-semibold sm:w-auto sm:min-w-[12rem]"
      >
        <Mail className="mr-2 size-4" aria-hidden="true" />
        Assign ticket to attendee
      </Button>
    </form>
  );
}
